import React, { useCallback, useContext, useState } from 'react';
import PropTypes from 'prop-types';
import * as Shared from '../../../VHSharedInputArea.styled';
import IconLabelButton, {
  IconLabelButtonTypes,
} from '../../../../molecules/IconLabelButton/IconLabelButton';
import VHTextChatInput from '../../../../VHChatInputArea/VHTextChatInput/VHTextChatInput';
import { useDispatch, useSelector } from 'react-redux';
import {
  ChatStates,
  addUserChat,
  isIteratingBranchingNodesSelector,
  isResponsiveSelector,
  selectChatState,
  sendBranchingAIText,
  sendBranchingFreeText,
  sendUnresponsiveMessage,
  virtualHumanTalkingSelector,
} from '../../../../../slices/chat/ChatSlice';
import { virtualHumanDataSelector } from '../../../../../slices/VirtualHumanSlice';
import { Icon } from '@virtidev/toolbox';

const FreeTextInputEditor = ({ nodeAction }) => {
  const chatState = useSelector(selectChatState);
  const vh = useSelector(virtualHumanDataSelector);
  const [freeTextChat, setFreeTextChat] = useState('');
  const isResponsive = useSelector(isResponsiveSelector);
  const virtualHumanTalking = useSelector(virtualHumanTalkingSelector);
  const dispatch = useDispatch();
  const isIteratingBranchingNodes = useSelector(
    isIteratingBranchingNodesSelector
  );
  const chatLoadingStatus = useSelector((state) => state.chat.status);
  let freeTextDisabled =
    chatState === ChatStates.LOADING ||
    chatState === ChatStates.COMPLETE ||
    chatLoadingStatus === 'pending' ||
    virtualHumanTalking;
  const submitFreeTextDisabled =
    freeTextDisabled || freeTextChat === '' || isIteratingBranchingNodes;
  const freeTextRecordingDisabled =
    freeTextDisabled || isIteratingBranchingNodes;

  const completingSessionStatus = useSelector(
    (state) => state.chat.completingSessionStatus
  );

  const submitFreeText = useCallback(
    (text) => {
      if (text === '') return;
      if (chatLoadingStatus === 'idle' && completingSessionStatus === 'idle') {
        dispatch(addUserChat({ text }));
        if (isResponsive) {
          if (nodeAction === 'ai') {
            dispatch(
              sendBranchingAIText({
                text,
                vhId: vh.ExternalID,
              })
            );
          } else {
            dispatch(
              sendBranchingFreeText({
                freeText: text,
                vhId: vh.ExternalID,
              })
            );
          }
        } else {
          dispatch(sendUnresponsiveMessage());
        }
      }
      setFreeTextChat('');
    },
    [
      dispatch,
      chatLoadingStatus,
      completingSessionStatus,
      isResponsive,
      vh.ExternalID,
      nodeAction,
    ]
  );

  const [isAwaitingTranscriptionToSend, setIsAwaitingTranscriptionToSend] =
    useState(false);
  const [isPendingTranscription, setIsPendingTranscription] = useState(false);
  const handlePendingTranscription = useCallback((pending) => {
    setIsPendingTranscription(pending);
  }, []);

  const handleSubmit = useCallback(() => {
    if (isPendingTranscription) {
      setIsAwaitingTranscriptionToSend(true);
    } else {
      submitFreeText(freeTextChat);
    }
  }, [submitFreeText, isPendingTranscription, freeTextChat]);

  const handleChange = useCallback(
    (newString) => {
      if (newString.indexOf('\n') > -1) {
        return;
      }
      // if we're awaitng a PTT transcription to finish (a hacky check based on the length of string change)
      const isMicTranscriptionChange =
        newString.length - freeTextChat.length > 1;
      if (isAwaitingTranscriptionToSend && isMicTranscriptionChange) {
        setIsAwaitingTranscriptionToSend(false);
        submitFreeText(newString);
      } else {
        setFreeTextChat(newString);
      }
    },
    [freeTextChat.length, isAwaitingTranscriptionToSend, submitFreeText]
  );

  const submitDisabled =
    freeTextDisabled ||
    virtualHumanTalking ||
    chatLoadingStatus === 'pending' ||
    freeTextChat === '' ||
    isAwaitingTranscriptionToSend;

  return (
    <Shared.ChatInputWrapper $disabled={freeTextRecordingDisabled}>
      <VHTextChatInput
        chat={freeTextChat}
        onSubmit={submitFreeText}
        onChange={handleChange}
        textDisabled={freeTextDisabled}
        submitDisabled={submitFreeTextDisabled}
        recordingDisabled={freeTextRecordingDisabled}
        onPendingTranscription={handlePendingTranscription}
        maxLength={512}
        inputDisabledText="Loading..."
      />
      <Shared.ExtraWrapper>
        <Shared.SendButton onClick={handleSubmit} disabled={submitDisabled}>
          <Icon icon="arrow" />
        </Shared.SendButton>
      </Shared.ExtraWrapper>
    </Shared.ChatInputWrapper>
  );
};

FreeTextInputEditor.propTypes = {
  nodeAction: PropTypes.string.isRequired,
};

export default FreeTextInputEditor;
